import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Papa from 'papaparse';
import { useData } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';
import EditParameterModal from './EditParameterModal';
import TableWithPagination from './TableWithPagination';
import Sidebar from './Sidebar';
import { 
  LineChart, Line, BarChart, Bar, ScatterChart, Scatter, XAxis, YAxis, 
  CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';

const KatalogVisualisasi = ({ activeTabProp }) => {
  const [activeTab, setActiveTab] = useState(activeTabProp || 'visualisasi');  
  const navigate = useNavigate();
  const { isLoading, error, executeQuery } = useData();
  const { user, isAdmin, isAnalis, isEndUser } = useAuth();
  
  const [previewVisualisasi, setPreviewVisualisasi] = useState(null);
  const [previewParameters, setPreviewParameters] = useState({ xAxis: '', yAxis: '', groupBy: '' });
  const [previewChartType, setPreviewChartType] = useState('');
  const [previewChartData, setPreviewChartData] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(false);
  
  const [visualisasiData, setVisualisasiData] = useState([]);
  const [availableDatabases, setAvailableDatabases] = useState([]);
  const [selectedDatabase, setSelectedDatabase] = useState('');

  const [showEditParameterModal, setShowEditParameterModal] = useState(false);
  const [editingVisualisasi, setEditingVisualisasi] = useState(null);
  const [localTampilkanTabel, setLocalTampilkanTabel] = useState(false);
  const [savingTampilkanTabel, setSavingTampilkanTabel] = useState(false);
  const [exportingCsvId, setExportingCsvId] = useState(null);

  useEffect(() => {
    if (activeTabProp) setActiveTab(activeTabProp);
  }, [activeTabProp]);

  const fetchAvailableDatabases = async () => {
    try {
      const response = await fetch(`/api/databases`);
      if (response.ok) {
        const databases = await response.json();
        setAvailableDatabases(databases);
        if (databases.length > 0) {
          setSelectedDatabase(databases[0].id_database);
        }
      }
    } catch (error) {
      console.error('Error fetching databases:', error);
    }
  };

  useEffect(() => {
    fetchAvailableDatabases();
  }, []);
  
  const parseChartData = (data, xKey, yKey, chartType) => {
    return data.map(item => {
      const parsedItem = { ...item };
      if (yKey && parsedItem[yKey] !== undefined && parsedItem[yKey] !== null) {
        const numValue = parseFloat(parsedItem[yKey]);
        if (!isNaN(numValue)) parsedItem[yKey] = numValue;
      }
      if (chartType === 'scatter' && xKey && parsedItem[xKey] !== undefined && parsedItem[xKey] !== null) {
        const numValue = parseFloat(parsedItem[xKey]);
        if (!isNaN(numValue)) parsedItem[xKey] = numValue;
      }
      return parsedItem;
    });
  };
  
  useEffect(() => {
    const fetchVisualisasiData = async () => {
      try {
        const response = await fetch(`/api/database/${selectedDatabase}/visualisasi`);
        if (response.ok) {
          const visualisasi = await response.json();
          const sortedVisualisasi = [...visualisasi].sort((a, b) =>
            new Date(b.created_at) - new Date(a.created_at)
          );
          setVisualisasiData(sortedVisualisasi);
        }
      } catch (err) {
        console.error('Error fetching visualisasi data:', err);
      }
    };
    if (selectedDatabase) fetchVisualisasiData();
  }, [selectedDatabase]);
  
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString('id-ID', options);
  };

  const handleEditParameter = (visualisasi) => {
    setEditingVisualisasi(visualisasi);
    setShowEditParameterModal(true);
  };

  const handleSaveParameterEdit = async (newChartData, params) => {
    try {
      const paramsResponse = await fetch(`/api/visualizations/${editingVisualisasi.id_visualisasi}/parameters`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ parameter_query: JSON.stringify(params) }),
      });

      if (paramsResponse.ok) {
        setVisualisasiData(prev => prev.map(vis =>
          vis.id_visualisasi === editingVisualisasi.id_visualisasi
            ? { ...vis, parameter_query: JSON.stringify(params) }
            : vis
        ));

        if (previewVisualisasi && previewVisualisasi.id_visualisasi === editingVisualisasi.id_visualisasi) {
          const parsedData = parseChartData(newChartData, previewParameters.xAxis, previewParameters.yAxis, editingVisualisasi.jenis_grafik);
          setPreviewChartData(parsedData);
          setPreviewVisualisasi({ ...previewVisualisasi, parameter_query: JSON.stringify(params) });
        }

        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        throw new Error('Failed to update visualization');
      }
    } catch (error) {
      console.error('Error updating visualization:', error);
      setSaveError(true);
      setTimeout(() => setSaveError(false), 3000);
    }
  };

  const handleSaveTampilkanTabel = async () => {
    setSavingTampilkanTabel(true);
    try {
      const response = await fetch(`/api/visualizations/${previewVisualisasi.id_visualisasi}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tampilkan_tabel: localTampilkanTabel ? 1 : 0 })
      });
      if (!response.ok) throw new Error('Gagal menyimpan');
      const newVal = localTampilkanTabel ? 1 : 0;
      setVisualisasiData(prev => prev.map(v =>
        v.id_visualisasi === previewVisualisasi.id_visualisasi ? { ...v, tampilkan_tabel: newVal } : v
      ));
      setPreviewVisualisasi(prev => ({ ...prev, tampilkan_tabel: newVal }));
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (e) {
      setSaveError(true);
      setTimeout(() => setSaveError(false), 3000);
    } finally {
      setSavingTampilkanTabel(false);
    }
  };

  const handlePreviewVisualisasi = async (visualisasi) => {
    const params = {
      xAxis: visualisasi.parameter_x,
      yAxis: visualisasi.parameter_y,
      groupBy: visualisasi.group_by || ''
    };

    try {
      if (previewVisualisasi && previewVisualisasi.id_visualisasi === visualisasi.id_visualisasi) {
        setPreviewVisualisasi(null);
        setPreviewChartData([]);
        setPreviewError('');
        return;
      }
      setPreviewVisualisasi(visualisasi);
      setLocalTampilkanTabel(!!visualisasi.tampilkan_tabel);
      setPreviewParameters(params);
      setPreviewChartType(visualisasi.jenis_grafik);
      setPreviewChartData([]);
      setPreviewError('');
      setPreviewLoading(true);

      const result = await executeVisualizationQuery(visualisasi);
      const parsedData = parseChartData(result, params.xAxis, params.yAxis, visualisasi.jenis_grafik);
      setPreviewChartData(parsedData);
    } catch (error) {
      console.error('Error previewing visualization:', error);
      setPreviewChartData([]);
      setPreviewError(error.message || 'Gagal memuat data visualisasi');
    } finally {
      setPreviewLoading(false);
    }
  };
  
  const handleDeleteVisualisasi = async (id) => {
    if (!isAdmin() && !isAnalis()) {
      alert('Anda tidak memiliki izin untuk menghapus visualisasi!');
      return;
    }
    if (window.confirm('Apakah Anda yakin ingin menghapus visualisasi ini?')) {
      try {
        const response = await fetch(`/api/visualizations/${id}`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
        });
        const responseData = await response.text();
        if (!response.ok) {
          let errorMessage = 'Gagal menghapus visualisasi';
          try {
            const errorData = JSON.parse(responseData);
            errorMessage = errorData.error || errorData.message || errorMessage;
            if (response.status === 403 && errorData.usage_count) {
              errorMessage = `Visualisasi tidak dapat dihapus karena sedang digunakan dalam salah satu analisis`;
            }
          } catch (e) {
            errorMessage = responseData || errorMessage;
          }
          throw new Error(errorMessage);
        }
        setVisualisasiData(visualisasiData.filter(item => item.id_visualisasi !== id));
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } catch (err) {
        console.error('Error deleting visualisasi:', err);
        alert(`${err.message}`);
        setSaveError(true);
        setTimeout(() => setSaveError(false), 3000);
      }
    }
  };

  const handleCreateVisualisasi = () => {
    if (!isAdmin() && !isAnalis()) {
      alert('Anda tidak memiliki izin untuk membuat visualisasi!');
      return;
    }
    navigate('/visualisasi/buat');
  };

  const handleFullEditVisualisasi = (visualisasi) => {
    if (!isAdmin() && !isAnalis()) {
      alert('Anda tidak memiliki izin untuk mengedit visualisasi!');
      return;
    }
    navigate(`/visualisasi/edit/${visualisasi.id_visualisasi}`);
  };

  const parseParameterQuery = (parameterQuery) => {
    if (!parameterQuery) return {};
    try {
      const params = typeof parameterQuery === 'string'
        ? JSON.parse(parameterQuery)
        : parameterQuery;
      return params && typeof params === 'object' ? params : {};
    } catch (error) {
      console.error('Error parsing visualization parameters:', error);
      return {};
    }
  };

  const getVisualizationDatabaseId = (visualisasi) => (
    visualisasi?.penyimpanan_database || visualisasi?.database || selectedDatabase || null
  );

  const executeVisualizationQuery = async (visualisasi) => {
    if (!visualisasi?.query_sql) return [];
    const params = parseParameterQuery(visualisasi.parameter_query);
    const databaseId = getVisualizationDatabaseId(visualisasi);
    const result = await executeQuery(visualisasi.query_sql, params, databaseId);
    return Array.isArray(result) ? result : [];
  };

  const getSafeCsvFileName = (visualisasi) => {
    const title = visualisasi?.judul || `visualisasi-${visualisasi?.id_visualisasi || 'tabel'}`;
    const safeTitle = title
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/gi, '_')
      .replace(/^_+|_+$/g, '');
    return `${safeTitle || 'visualisasi_tabel'}.csv`;
  };

  const handleExportCsv = async (visualisasi) => {
    if (visualisasi.jenis_grafik !== 'table') {
      alert('Hanya visualisasi berbentuk tabel yang dapat diekspor ke CSV.');
      return;
    }

    setExportingCsvId(visualisasi.id_visualisasi);
    try {
      const rows = await executeVisualizationQuery(visualisasi);
      if (rows.length === 0) {
        alert('Data tabel kosong, tidak ada data untuk diekspor.');
        return;
      }

      const csv = Papa.unparse(rows);
      const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = getSafeCsvFileName(visualisasi);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert(`Gagal mengekspor CSV: ${error.message}`);
    } finally {
      setExportingCsvId(null);
    }
  };
  
  const renderParameterSubtitle = (parameterQuery) => {
    if (!parameterQuery) return null;
    try {
      const params = typeof parameterQuery === 'string' ? JSON.parse(parameterQuery) : parameterQuery;
      const entries = Object.entries(params).filter(([, v]) => v !== null && v !== undefined && v !== '');
      if (entries.length === 0) return null;
      return (
        <div style={{ fontSize: '0.85rem', color: '#555', marginTop: '4px', fontStyle: 'italic' }}>
          {entries.map(([k, v]) => (
            <span key={k} style={{ marginRight: '12px' }}>
              Untuk parameter <strong>{k}</strong> = <strong>{v}</strong>
            </span>
          ))}
        </div>
      );
    } catch {
      return null;
    }
  };

  const renderChart = (isPreview = false) => {
    if (isPreview && previewVisualisasi && previewChartData.length > 0) {
      if (previewChartType === 'table') {
        return <TableWithPagination data={previewChartData} />;
      }

      const dataKey = previewParameters.xAxis;
      const valueKey = previewParameters.yAxis;
      const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658', '#8DD1E1'];
      return (
        <ResponsiveContainer width="100%" height={400}>
          {previewChartType === 'bar' ? (
            <BarChart data={previewChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey={dataKey} />
              <YAxis />
              <Tooltip />
              <Legend payload={[
                { value: `Sumbu X: ${dataKey}`, type: 'line', color: '#666' },
                { value: `Sumbu Y: ${valueKey}`, type: 'rect', color: '#8884d8' }
              ]} />
              <Bar dataKey={valueKey} fill="#8884d8" name={valueKey} />
            </BarChart>
          ) : previewChartType === 'line' ? (
            <LineChart data={previewChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey={dataKey} />
              <YAxis />
              <Tooltip />
              <Legend payload={[
                { value: `Sumbu X: ${dataKey}`, type: 'line', color: '#666' },
                { value: `Sumbu Y: ${valueKey}`, type: 'line', color: '#8884d8' }
              ]} />
              <Line type="monotone" dataKey={valueKey} stroke="#8884d8" name={valueKey} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
            </LineChart>
          ) : previewChartType === 'pie' ? (
            <PieChart>
              <Pie data={previewChartData} cx="50%" cy="50%" labelLine={true} outerRadius={150} fill="#8884d8" dataKey={valueKey} nameKey={dataKey} label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}>
                {previewChartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend payload={[
                { value: `Category: ${dataKey}`, type: 'rect', color: '#666' },
                { value: `Values: ${valueKey}`, type: 'rect', color: '#8884d8' }
              ]} />
            </PieChart>
          ) : (
            <ScatterChart>
              <CartesianGrid />
              <XAxis type="number" dataKey={dataKey} name={dataKey} />
              <YAxis type="number" dataKey={valueKey} name={valueKey} />
              <Tooltip cursor={{ strokeDasharray: '3 3' }} />
              <Legend payload={[
                { value: `Sumbu X: ${dataKey}`, type: 'rect', color: '#666' },
                { value: `Sumbu Y: ${valueKey}`, type: 'rect', color: '#8884d8' }
              ]} />
              <Scatter name={`${dataKey} vs ${valueKey}`} data={previewChartData} fill="#8884d8" />
            </ScatterChart>
          )}
        </ResponsiveContainer>
      );
    }
    return <div style={{ padding: '20px', textAlign: 'center' }}>Tidak ada data untuk ditampilkan</div>;
  };

  return (
    <div className="dashboard-container">
      <Sidebar activeTabProp={activeTab}>
        {isLoading ? (
          <div className="loading"><div className="loading-spinner"></div><p>Memuat data...</p></div>
        ) : error ? (
          <div className="error"><i className="fas fa-exclamation-triangle"></i> Error: {error}</div>
        ) : (
          <div className="dashboard-content">
            <div className="data-explorer">
              <h1>Katalog Visualisasi</h1>

              <div className="database-viewing-section" style={{ marginBottom: '20px' }}>
                <h2>Pilih Database untuk Dilihat</h2>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                  <label style={{ marginRight: '10px', fontWeight: 'bold' }}>Database:</label>
                  <select
                    value={selectedDatabase}
                    onChange={(e) => setSelectedDatabase(parseInt(e.target.value))}
                    style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid #ddd', backgroundColor: 'white', minWidth: '200px' }}
                  >
                    {availableDatabases.map((database) => (
                      <option key={database.id_database} value={database.id_database}>
                        {database.name} ({database.host}:{database.port || 'default'})
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                  Jumlah visualisasi: {visualisasiData.length}
                </div>
              </div>
              
              <div className="catalog-section">
                <h2>Katalog Visualisasi</h2>
                {saveSuccess && <div className="save-success-message">Operasi berhasil!</div>}
                
                {visualisasiData.length === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', backgroundColor: '#f5f5f5', borderRadius: '4px', margin: '20px 0' }}>
                    <p>Tidak ada visualisasi yang tersedia untuk database ini.</p>
                    {(isAdmin() || isAnalis()) && (
                      <button className="create-button" style={{ marginTop: '10px', padding: '10px 20px', backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '16px' }} onClick={handleCreateVisualisasi}>
                        Buat Visualisasi Pertama
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="data-table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Judul</th>
                          <th className="description-column">Deskripsi</th>
                          <th>Jenis Grafik</th>
                          <th>Tanggal Dibuat</th>
                          <th>Aksi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visualisasiData.map((item) => (
                          <tr key={item.id_visualisasi}>
                            <td>{item.judul}</td>
                            <td>{item.deskripsi}</td>
                            <td>{item.jenis_grafik}</td>
                            <td>{formatDate(item.created_at)}</td>
                            <td>
                              <div className="action-buttons">
                                <button
                                  className={previewVisualisasi && previewVisualisasi.id_visualisasi === item.id_visualisasi ? "cancel-button" : "view-button"}
                                  onClick={() => handlePreviewVisualisasi(item)}
                                >
                                  {previewVisualisasi && previewVisualisasi.id_visualisasi === item.id_visualisasi ? "Tutup" : "Lihat"}
                                </button>
                                {item.jenis_grafik === 'table' && (
                                  <button
                                    className="view-button"
                                    onClick={(e) => { e.stopPropagation(); handleExportCsv(item); }}
                                    disabled={exportingCsvId === item.id_visualisasi}
                                    style={{ backgroundColor: '#0f766e' }}
                                  >
                                    {exportingCsvId === item.id_visualisasi ? 'Exporting...' : 'Export CSV'}
                                  </button>
                                )}
                                {(isEndUser() || isAnalis()) && item.query_sql && item.query_sql.includes(':') && (
                                  <button className="view-button" onClick={(e) => { e.stopPropagation(); handleEditParameter(item); }} style={{ backgroundColor: '#2196F3' }}>
                                    Edit
                                  </button>
                                )}
                                {(isAdmin() || isAnalis()) && (
                                  <button className="view-button" onClick={(e) => { e.stopPropagation(); handleFullEditVisualisasi(item); }} style={{ backgroundColor: '#ff9800' }}>
                                    Edit Penuh
                                  </button>
                                )}
                                {(isAdmin() || isAnalis()) && (
                                  <button className="delete-button" onClick={(e) => { e.stopPropagation(); handleDeleteVisualisasi(item.id_visualisasi); }}>
                                    Hapus
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                  
                {previewVisualisasi && (
                  <div className="preview-section" style={{ marginTop: '20px', border: '1px solid #ddd', borderRadius: '4px', padding: '15px' }}>
                    <h2>Preview Visualisasi</h2>
                    <div className="preview-header">
                      <h3>{previewVisualisasi.judul}</h3>
                      {previewVisualisasi.deskripsi && (
                        <p className="visualization-description">{previewVisualisasi.deskripsi}</p>
                      )}
                      {renderParameterSubtitle(previewVisualisasi.parameter_query)}
                    </div>

                    {/* Toggle tampilkan tabel — hanya untuk non-table */}
                    {previewVisualisasi.jenis_grafik === 'table' && (
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
                        <button
                          type="button"
                          className="download-button"
                          onClick={() => handleExportCsv(previewVisualisasi)}
                          disabled={exportingCsvId === previewVisualisasi.id_visualisasi || previewLoading}
                          style={{
                            marginTop: 0,
                            opacity: (exportingCsvId === previewVisualisasi.id_visualisasi || previewLoading) ? 0.7 : 1,
                            cursor: (exportingCsvId === previewVisualisasi.id_visualisasi || previewLoading) ? 'not-allowed' : 'pointer'
                          }}
                        >
                          {exportingCsvId === previewVisualisasi.id_visualisasi ? 'Exporting...' : 'Export CSV'}
                        </button>
                      </div>
                    )}

                    {previewVisualisasi.jenis_grafik !== 'table' && (
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{
                          fontSize: '0.8rem', padding: '3px 8px', borderRadius: '4px',
                          backgroundColor: localTampilkanTabel ? '#e8f5e9' : '#fafafa',
                          color: localTampilkanTabel ? '#2e7d32' : '#888',
                          border: `1px solid ${localTampilkanTabel ? '#a5d6a7' : '#ddd'}`
                        }}>
                          {localTampilkanTabel ? 'Tabel: Aktif ✓' : 'Tabel: Nonaktif'}
                        </span>

                        <button
                          type="button"
                          onClick={() => setLocalTampilkanTabel(prev => !prev)}
                          style={{ padding: '4px 10px', borderRadius: '4px', border: '1px solid #ddd', cursor: 'pointer', backgroundColor: '#f5f5f5', fontSize: '0.8rem' }}
                        >
                          {localTampilkanTabel ? 'Nonaktifkan' : 'Aktifkan'}
                        </button>

                        {localTampilkanTabel !== !!previewVisualisasi.tampilkan_tabel && (
                          <button
                            type="button"
                            onClick={handleSaveTampilkanTabel}
                            disabled={savingTampilkanTabel}
                            style={{ padding: '4px 10px', borderRadius: '4px', border: 'none', cursor: savingTampilkanTabel ? 'not-allowed' : 'pointer', backgroundColor: '#4CAF50', color: 'white', fontSize: '0.8rem', opacity: savingTampilkanTabel ? 0.7 : 1 }}
                          >
                            {savingTampilkanTabel ? 'Menyimpan...' : 'Simpan'}
                          </button>
                        )}
                      </div>
                    )}

                    <div className={previewVisualisasi.jenis_grafik !== 'table' ? 'chart-container' : ''}>
                      {previewLoading ? (
                        <div style={{ padding: '20px', textAlign: 'center' }}>Memuat data terbaru...</div>
                      ) : previewError ? (
                        <div className="query-error">{previewError}</div>
                      ) : (
                        renderChart(true)
                      )}
                    </div>

                    {(localTampilkanTabel && previewVisualisasi.jenis_grafik !== 'table' && previewChartData.length > 0) && (
                      <div style={{ marginTop: '20px' }}>
                        <h4 style={{ marginBottom: '8px' }}>Data</h4>
                        <TableWithPagination data={previewChartData} />
                      </div>
                    )}
                  </div>
                )}
                  
                {(isAdmin() || isAnalis()) && visualisasiData.length > 0 && (
                  <div className="button-container" style={{ marginTop: '20px', textAlign: 'center' }}>
                    <button className="create-button" style={{ padding: '10px 20px', backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '16px' }} onClick={handleCreateVisualisasi}>
                      Buat Visualisasi
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Sidebar>
      
      <EditParameterModal
        isOpen={showEditParameterModal}
        onClose={() => setShowEditParameterModal(false)}
        visualisasi={editingVisualisasi}
        onSave={handleSaveParameterEdit}
      />
    </div>
  );
};

export default KatalogVisualisasi;
